import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessagesSquare, Upload } from 'lucide-react';
import {
  EmptyState,
  GlassCard,
  PageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpAnchor } from '@/design-system/HelpAnchor';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { SEED_THREADS } from '@/versions/v1/shared/thread-seed';
import { SEED_FILES } from '@/versions/v1/shared/files-seed';
import { formatRelativeTime } from '@/versions/v1/modules/settings/format-utils';

export function ProjectHomeScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);

  const projectThreads = useMemo(
    () =>
      SEED_THREADS.filter((thread) => thread.projectSlug === projectId).sort(
        (a, b) => b.updatedAt - a.updatedAt,
      ),
    [projectId],
  );

  const previewFiles = useMemo(() => SEED_FILES.slice(0, 4), []);

  useEffect(() => {
    document.title = project ? `${project.name} · Foundation` : 'Project · Foundation';
  }, [project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <PageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectHomeScene">
        <SurfacePageBody className="space-y-8" width="full">
          <div className="flex items-start justify-between gap-4">
            <PageHeader
              kicker="Project"
              title={project.name}
              description={project.description}
              titleSize="large"
            />
            <HelpAnchor doc="workspace/project-home" label="Project home" />
          </div>

          <GlassCard tone="strong" className="flex flex-col gap-3 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Start a chat in {project.name}
              </p>
              <p className="mt-1 text-xs text-muted">
                Inherits this project&apos;s instructions, memory, and knowledge files.
              </p>
            </div>
            <Link to={projectSidebarHref(project.slug, 'chat')} className="btn-primary inline-flex h-9 items-center px-4">
              Start chat
            </Link>
          </GlassCard>

          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted">Chats</h2>
              <span className="text-xs text-muted">{projectThreads.length} total</span>
            </div>
            {projectThreads.length === 0 ? (
              <EmptyState
                icon={<MessagesSquare className="h-8 w-8 text-zinc-400" />}
                title="No chats in this project yet"
                description="Start one above — new chats pick up project context."
                action={
                  <Link
                    to={projectSidebarHref(project.slug, 'chat')}
                    className="btn-ghost inline-flex h-9 items-center px-3 text-sm"
                  >
                    Start your first chat
                  </Link>
                }
              />
            ) : (
              <GlassCard tone="strong" className="overflow-hidden">
                <ul className="divide-y divide-[var(--glass-border)]">
                  {projectThreads.map((thread) => (
                    <li key={thread.id}>
                      <Link
                        to={projectSidebarHref(project.slug, 'chat')}
                        className="flex items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                      >
                        <span className="truncate font-medium">{thread.title}</span>
                        <span className="flex-shrink-0 text-xs text-muted">
                          {formatRelativeTime(thread.updatedAt)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted">Files</h2>
              <span className="text-xs text-muted">{project.fileCount} total</span>
            </div>
            <GlassCard tone="strong" className="overflow-hidden">
              <ul className="divide-y divide-[var(--glass-border)]">
                {previewFiles.map((file) => (
                  <li
                    key={file.id}
                    className="flex items-center gap-3 px-5 py-3 text-sm"
                  >
                    <file.Icon className="h-4 w-4 text-zinc-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{file.name}</div>
                      <div className="text-xs text-muted">{file.meta}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-[var(--glass-border)] px-5 py-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Upload file
                </button>
              </div>
            </GlassCard>
          </section>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
